/**
 * Clavier (KeyboardEvent.code : position physique, donc AZERTY et QWERTY sans bascule), souris en Pointer Lock,
 * manette (Gamepad API, mapping standard). Produit des PlayerInput pour la simulation ; ne la modifie jamais.
 */
import { emptyInput, type PlayerInput } from '../sim/pilots/player';
import type { Settings } from '../ui/settings';

export type Device = 'keyboard' | 'gamepad' | 'mixed' | 'none';
export type UiAction = 'pause' | 'restart' | 'skipTutorial' | 'tuning' | 'debug' | 'autopilot' | 'menuUp' | 'menuDown' | 'menuLeft' | 'menuRight' | 'menuConfirm' | 'menuBack';

const DEAD_ZONE = 0.15;

/** Codes physiques : la touche nommée Z en AZERTY est KeyW. */
const BINDINGS = {
  forward: 'KeyW',
  backward: 'KeyS',
  left: 'KeyA',
  right: 'KeyD',
  up: 'Space',
  down: 'ControlLeft',
  rollLeft: 'KeyQ',
  rollRight: 'KeyE',
  boost: 'ShiftLeft',
  brake: 'KeyF',
  skipIntermission: 'KeyN',
  throttleZero: 'KeyX',
  jump: 'KeyJ',
  jumpCycle: 'KeyG',
} as const;

const UI_KEYS: Record<string, UiAction> = {
  KeyP: 'pause',
  KeyR: 'restart',
  KeyT: 'skipTutorial',
  F1: 'tuning',
  F3: 'debug',
  F8: 'autopilot',
  ArrowUp: 'menuUp',
  ArrowDown: 'menuDown',
  ArrowLeft: 'menuLeft',
  ArrowRight: 'menuRight',
  Enter: 'menuConfirm',
  Escape: 'menuBack',
};

export class InputManager {
  private readonly keys = new Set<string>();
  private mouseDx = 0;
  private mouseDy = 0;
  private mouseButtons = 0;
  private wheelSteps = 0;
  private gamepadButtonsPrev: boolean[] = [];
  private usedKeyboard = false;
  private usedGamepad = false;
  /** Dernier périphérique utilisé (télémétrie des contrôles). */
  lastDevice: 'keyboard' | 'gamepad' = 'keyboard';
  private uiListeners: ((action: UiAction) => void)[] = [];
  /** Vrai quand le jeu attend des commandes de vol (sinon les touches de menu ont la main). */
  flightActive = false;
  pointerLocked = false;
  /** Libellés de touches selon la disposition réelle, quand le navigateur les fournit. */
  private layoutMap: Map<string, string> | null = null;

  constructor(private readonly canvas: HTMLCanvasElement, private settings: Settings) {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', () => this.releaseAll());
    canvas.addEventListener('mousedown', this.onMouseDown);
    window.addEventListener('mouseup', this.onMouseUp);
    window.addEventListener('mousemove', this.onMouseMove);
    // Molette : accélérateur par crans (vers le haut = plus de poussée).
    canvas.addEventListener('wheel', (e) => {
      if (!this.flightActive) return;
      e.preventDefault();
      this.wheelSteps += e.deltaY < 0 ? 1 : e.deltaY > 0 ? -1 : 0;
    }, { passive: false });
    canvas.addEventListener('contextmenu', (e) => e.preventDefault());
    document.addEventListener('pointerlockchange', () => {
      this.pointerLocked = document.pointerLockElement === canvas;
      if (!this.pointerLocked) this.mouseButtons = 0;
    });
    const nav = navigator as Navigator & { keyboard?: { getLayoutMap?: () => Promise<Map<string, string>> } };
    nav.keyboard?.getLayoutMap?.().then((map) => { this.layoutMap = map; }).catch(() => undefined);
  }

  setSettings(settings: Settings): void {
    this.settings = settings;
  }

  onUi(listener: (action: UiAction) => void): void {
    this.uiListeners.push(listener);
  }

  /** Libellé affiché d'une touche physique (« Z » sur AZERTY pour KeyW). */
  keyLabel(code: string): string {
    const fromLayout = this.layoutMap?.get(code);
    if (fromLayout) return fromLayout.toUpperCase();
    const fallback: Record<string, string> = { Space: 'ESPACE', ControlLeft: 'CTRL G', ShiftLeft: 'MAJ G' };
    return fallback[code] ?? code.replace(/^Key/, '');
  }

  bindingLabel(action: keyof typeof BINDINGS): string {
    return this.keyLabel(BINDINGS[action]);
  }

  requestPointerLock(): void {
    if (document.pointerLockElement === this.canvas) return;
    const el = this.canvas as HTMLCanvasElement & { requestPointerLock: (opts?: { unadjustedMovement?: boolean }) => Promise<void> | void };
    try {
      const result = el.requestPointerLock({ unadjustedMovement: true });
      if (result instanceof Promise) result.catch(() => { void Promise.resolve(el.requestPointerLock()).catch(() => undefined); });
    } catch {
      el.requestPointerLock();
    }
  }

  exitPointerLock(): void {
    if (document.pointerLockElement) document.exitPointerLock();
  }

  /** Vide les états tenus (changement de fenêtre, ouverture d'un menu). */
  releaseAll(): void {
    this.keys.clear();
    this.mouseButtons = 0;
    this.mouseDx = 0;
    this.mouseDy = 0;
    this.wheelSteps = 0;
  }

  device(): Device {
    if (this.usedKeyboard && this.usedGamepad) return 'mixed';
    if (this.usedGamepad) return 'gamepad';
    if (this.usedKeyboard) return 'keyboard';
    return 'none';
  }

  /**
   * Entrées du prochain tick. `consumeMouse` : le delta souris accumulé n'est donné qu'au premier tick
   * d'une image, pour ne pas le compter plusieurs fois.
   */
  sample(consumeMouse: boolean): PlayerInput {
    const input = emptyInput();
    if (!this.flightActive) return input;
    const k = (code: string) => this.keys.has(code);
    const forwardKeys = (k(BINDINGS.forward) ? 1 : 0) - (k(BINDINGS.backward) ? 1 : 0);
    if (this.settings.throttleMode === 'throttle') input.throttleAxis = forwardKeys;
    else input.thrust.z = forwardKeys;
    input.throttleZero = k(BINDINGS.throttleZero);
    if (consumeMouse) {
      input.throttleStep = this.wheelSteps;
      this.wheelSteps = 0;
    }
    input.thrust.x = (k(BINDINGS.right) ? 1 : 0) - (k(BINDINGS.left) ? 1 : 0);
    input.thrust.y = (k(BINDINGS.up) ? 1 : 0) - (k(BINDINGS.down) ? 1 : 0);
    input.roll = (k(BINDINGS.rollRight) ? 1 : 0) - (k(BINDINGS.rollLeft) ? 1 : 0);
    input.boost = k(BINDINGS.boost);
    input.brake = k(BINDINGS.brake);
    input.skipIntermission = k(BINDINGS.skipIntermission);
    input.jump = k(BINDINGS.jump);
    input.jumpCycle = k(BINDINGS.jumpCycle);
    input.firePrimary = (this.mouseButtons & 1) !== 0;
    input.fireSecondary = (this.mouseButtons & 2) !== 0;

    if (consumeMouse) {
      const sens = this.settings.mouseSensitivity;
      input.mouseYawDeg = this.mouseDx * sens;
      // movementY < 0 quand la souris monte ; « inversé » (défaut Unreal) : souris haut = nez haut.
      input.mousePitchDeg = -this.mouseDy * sens * (this.settings.invertPitch ? 1 : -1);
      this.mouseDx = 0;
      this.mouseDy = 0;
    }
    this.applyGamepad(input);
    return input;
  }

  /** Lit la manette : axes et boutons de vol, actions d'interface sur front montant. */
  pollGamepad(): void {
    const pad = this.firstPad();
    if (!pad) return;
    const pressed = pad.buttons.map((b) => b.pressed);
    const edge = (i: number) => pressed[i] === true && this.gamepadButtonsPrev[i] !== true;
    const fire = (a: UiAction) => this.uiListeners.forEach((l) => l(a));
    if (edge(9)) fire('pause');
    if (edge(1)) fire(this.flightActive ? 'restart' : 'menuBack');
    if (edge(14)) fire('skipTutorial');
    if (!this.flightActive) {
      if (edge(12)) fire('menuUp');
      if (edge(13)) fire('menuDown');
      if (edge(14)) fire('menuLeft');
      if (edge(15)) fire('menuRight');
      if (edge(0)) fire('menuConfirm');
    }
    if (pressed.some(Boolean) || pad.axes.some((a) => Math.abs(a) > 0.3)) {
      this.usedGamepad = true;
      this.lastDevice = 'gamepad';
    }
    this.gamepadButtonsPrev = pressed;
  }

  private firstPad(): Gamepad | null {
    for (const pad of navigator.getGamepads?.() ?? []) if (pad?.connected) return pad;
    return null;
  }

  private applyGamepad(input: PlayerInput): void {
    const pad = this.firstPad();
    if (!pad) return;
    const axis = (i: number) => {
      const v = pad.axes[i] ?? 0;
      return Math.abs(v) < DEAD_ZONE ? 0 : (v - Math.sign(v) * DEAD_ZONE) / (1 - DEAD_ZONE);
    };
    const button = (i: number) => pad.buttons[i]?.pressed === true;
    const value = (i: number) => pad.buttons[i]?.value ?? 0;
    input.thrust.x = clamp(input.thrust.x + axis(0));
    input.thrust.z = clamp(input.thrust.z - axis(1));
    input.thrust.y = clamp(input.thrust.y + (button(3) ? 1 : 0) - (button(2) ? 1 : 0));
    input.lookYaw = axis(2);
    input.lookPitch = -axis(3) * (this.settings.invertPitch ? 1 : -1);
    input.roll = clamp(input.roll + (button(5) ? 1 : 0) - (button(4) ? 1 : 0));
    input.boost ||= button(10);
    input.brake ||= button(11);
    input.firePrimary ||= value(7) > 0.35;
    input.fireSecondary ||= value(6) > 0.35;
    input.skipIntermission ||= button(15);
    input.jump ||= button(8);
  }

  private onKeyDown = (e: KeyboardEvent) => {
    // Saisie de texte (nom du pilote, signalement) : le champ garde ses touches.
    const target = e.target as HTMLElement | null;
    if (target && (target.tagName === 'TEXTAREA' || (target.tagName === 'INPUT' && (target as HTMLInputElement).type === 'text'))) return;
    this.lastDevice = 'keyboard';
    const ui = UI_KEYS[e.code];
    if (ui) {
      if (!e.repeat) this.uiListeners.forEach((l) => l(ui));
      if (e.code.startsWith('F') || e.code === 'Escape' || this.flightActive) e.preventDefault();
      return;
    }
    if (this.flightActive) {
      this.keys.add(e.code);
      this.usedKeyboard = true;
      // Empêche le défilement (Espace) et les raccourcis du navigateur pendant le vol.
      if (e.code === 'Space' || e.code === 'ControlLeft' || e.code.startsWith('Arrow')) e.preventDefault();
    }
  };

  private onKeyUp = (e: KeyboardEvent) => {
    this.keys.delete(e.code);
  };

  private onMouseDown = (e: MouseEvent) => {
    if (!this.flightActive) return;
    if (!this.pointerLocked) {
      this.requestPointerLock();
      return;
    }
    this.usedKeyboard = true;
    if (e.button === 0) this.mouseButtons |= 1;
    if (e.button === 2) this.mouseButtons |= 2;
    e.preventDefault();
  };

  private onMouseUp = (e: MouseEvent) => {
    if (e.button === 0) this.mouseButtons &= ~1;
    if (e.button === 2) this.mouseButtons &= ~2;
  };

  private onMouseMove = (e: MouseEvent) => {
    if (!this.pointerLocked || !this.flightActive) return;
    this.lastDevice = 'keyboard';
    this.mouseDx += e.movementX;
    this.mouseDy += e.movementY;
  };
}

const clamp = (v: number) => Math.max(-1, Math.min(1, v));
